import { absoluteUrl, CANONICAL_ORIGIN } from './constants';

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Localingos',
    url: CANONICAL_ORIGIN,
    logo: `${CANONICAL_ORIGIN}/logo512.png`,
    /*
     * No `sameAs`. It used to assert https://github.com/juhsis/Localingos, which
     * returns 404 — the repository is not public. A sameAs pointing at a dead URL
     * is a claim about identity that cannot be verified, so it is worse than
     * omitting the property. If the repository is ever made public, add it back:
     * a real, browsable repo is the strongest trust signal this product can offer
     * a developer, and there is currently none anywhere on the site.
     */
  };
}

export function softwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Localingos',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web, CLI, GitHub Action, MCP',
    description: 'AI-powered localization platform. Translate your app into 56 locales with one CLI command.',
    url: CANONICAL_ORIGIN,
    /*
     * An AggregateOffer, because there is a genuinely free tier and three paid
     * plans. This was a single Offer with `price: '0'`, which told Google the whole
     * product is free — the richest possible result for a paid product, and wrong.
     *
     * highPrice is the Scale plan's actual annual charge. See productSchema below
     * for why these are annual figures rather than the monthly-equivalent numbers
     * shown on the pricing page.
     */
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: '948',
      priceCurrency: 'USD',
      offerCount: 4,
      description: 'Free tier with 5,000 words, then annual plans from $108/year.',
    },
  };
}

export function productSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Localingos',
    description: 'Developer-first localization platform with word-based pricing.',
    brand: { '@type': 'Brand', name: 'Localingos' },
    /*
     * `price` is the amount actually charged, which is the annual total.
     *
     * These were '9', '29' and '79' — the monthly-equivalent figures the pricing
     * page displays. But PlanConfig.java derives annualPrice() as monthlyPrice × 12
     * and there is a single Stripe price per plan, so the only transaction that
     * exists is the annual one: $108, $348, $948. A rich result advertising "$9"
     * ahead of a $108 charge is the kind of mismatch that loses the rich result and,
     * worse, the sale at the checkout screen.
     *
     * The monthly figure is not dropped — priceSpecification carries it as a unit
     * price billed 12 months at a time, which is exactly what is on offer.
     */
    offers: [
      {
        '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD',
        availability: 'https://schema.org/InStock', url: absoluteUrl('/pricing'),
        description: '5,000 words, no time limit, no credit card.',
      },
      ...([
        { name: 'Starter', monthly: '9',  annual: '108', words: '50,000' },
        { name: 'Growth',  monthly: '29', annual: '348', words: '250,000' },
        { name: 'Scale',   monthly: '79', annual: '948', words: '1,000,000' },
      ].map(plan => ({
        '@type': 'Offer',
        name: plan.name,
        price: plan.annual,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: absoluteUrl('/pricing'),
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: plan.monthly,
          priceCurrency: 'USD',
          unitCode: 'MON',
          billingDuration: 12,
          billingIncrement: 12,
        },
        description: `${plan.words} words per year. $${plan.monthly}/month, billed annually as $${plan.annual}.`,
      }))),
    ],
  };
}

export function breadcrumbSchema(crumbs: Array<{ name: string; path: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}

export function faqSchema(qas: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: qas.map(qa => ({
      '@type': 'Question',
      name: qa.question,
      acceptedAnswer: { '@type': 'Answer', text: qa.answer },
    })),
  };
}

export function blogPostingSchema(article: {
  title: string;
  description: string;
  date: string;
  author: string;
  slug: string;
  ogImage?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    image: article.ogImage ? `${CANONICAL_ORIGIN}${article.ogImage}` : undefined,
    mainEntityOfPage: `${CANONICAL_ORIGIN}/blog/${article.slug}`,
  };
}
